/**
 * Every application the signed-in tenant has open, and their saved rooms
 * (COL-533).
 *
 * `useTenantBooking` answers "which ONE booking is this person's tenancy" and
 * caps at one row. The applicant frame needs the opposite: all of them, so a
 * person applying to three rooms sees three cards with three rails.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import {
  CLOSED_APPLICATION_STATUSES,
  isClosedApplication,
} from "@/lib/tenant-application-stage";

/**
 * Keyed under the `active-booking` prefix on purpose.
 *
 * V1 and V2 both invalidate `["active-booking", user.id]` after anything that
 * changes a booking; React Query matches key prefixes, so this list refreshes
 * with them instead of going stale behind a mutation it never hears about.
 */
export const TENANT_APPLICATIONS_QUERY_KEY = "active-booking";

export function useTenantApplications() {
  const { user } = useAuth();

  return useQuery<any[]>({
    queryKey: [
      TENANT_APPLICATIONS_QUERY_KEY,
      user?.id,
      "tenant-v2-applications",
    ],
    enabled: !!user?.id,
    refetchInterval: 30_000,
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await (supabase as any)
        .from("bookings" as any)
        .select(
          `
          *,
          rooms (
            id,
            title,
            price,
            images,
            deposit_amount,
            available_from,
            properties (
              id,
              title,
              address,
              zip_code,
              city,
              image_url
            )
          )
        `
        )
        // An admin-archived booking is gone from the tenant's view even though
        // its status stays live — same rule as useTenantBooking.
        .is("admin_archived_at", null)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      /*
        Closed rows are filtered HERE, not with a `.not("status", "in", …)`.

        A PostgREST negated-IN silently returns rows whose status is NULL, and
        `isClosedApplication` is the same predicate the rail uses — one rule,
        so a status that stops counting as closed cannot start showing up in
        one place and not the other.
      */
      return ((data as any[]) ?? []).filter((b) => !isClosedApplication(b));
    },
  });
}

/** Exposed for tests and for anything that needs the same exclusion list. */
export { CLOSED_APPLICATION_STATUSES };

/**
 * The tenant's saved rooms.
 *
 * `favorites` is (user_id, room_id) with owner-only RLS, so this is already
 * scoped by policy; the explicit `eq` keeps it scoped on the client too rather
 * than relying on the policy alone. V1 does this inline in two different pages
 * (`Dashboard.tsx` and `Properties.tsx`) with the same query key — this is the
 * first shared version, and it keeps that key so all three stay in sync.
 */
export function useSavedRooms() {
  const { user } = useAuth();

  return useQuery<any[]>({
    queryKey: ["favorites", user?.id, "tenant-v2"],
    enabled: !!user?.id,
    queryFn: async () => {
      if (!user?.id) return [];
      const { data: favorites, error: favoritesError } = await (supabase as any)
        .from("favorites" as any)
        .select("room_id")
        .eq("user_id", user.id);
      if (favoritesError) throw favoritesError;

      const ids = ((favorites as any[]) ?? [])
        .map((f) => f?.room_id)
        .filter((id) => id != null);
      if (ids.length === 0) return [];

      const { data: rooms, error: roomsError } = await (supabase as any)
        .from("rooms" as any)
        .select(
          `
          id,
          title,
          price,
          images,
          available,
          available_from,
          next_available_date,
          size_sqm,
          properties (
            id,
            title,
            address,
            zip_code,
            city,
            image_url
          )
        `
        )
        .in("id", ids);
      if (roomsError) throw roomsError;
      return (rooms as any[]) ?? [];
    },
  });
}

/**
 * Un-save a room from the Favoriten tab.
 *
 * V1 has this mutation inline in `Dashboard.tsx` AND again in
 * `Properties.tsx`; this is the third call site, so it goes in the shared
 * module rather than being hand-rolled a third time — the two V1 copies are
 * worth folding in here as a follow-up.
 *
 * `favorites` is owner-only under RLS, and the explicit `user_id` filter keeps
 * it scoped on the client too rather than trusting the policy alone to decide
 * whose row a delete reaches.
 */
export function useUnsaveRoom() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roomId: number | string) => {
      if (!user?.id) throw new Error("not signed in");
      const { error } = await (supabase as any)
        .from("favorites" as any)
        .delete()
        .eq("user_id", user.id)
        .eq("room_id", roomId);
      if (error) throw error;
    },
    // Prefix-matched, so V1's own ["favorites", uid] list refreshes too.
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: ["favorites", user?.id] }),
  });
}
